/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { FormulaContextLoader, REPORT_HOUSE_PLAN_BILL_OF_MATERIALS, ReportService } from '@gh/builder-shared';
import {
  ComponentPropagateFormulaRequestCustomDto,
  ComponentRequest,
  GtoUom,
  HousePlanEstimateRestService,
  HousePlanRestService,
  JobRestService,
  reportIdField, TakeOffParamsRestService,
  UomRestService,
} from '@gh/builder-shared-data';
import { createDialogRoute, DictionaryService, InitViewService } from '@gh/common-shared';
import { AutocompleteItem, ID, withDisplayAsNameAndDescription } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { expandObservable } from '@gh/core-util';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { FormulaPropagationDialogComponent } from '../components/formula-propagation-dialog/formula-propagation-dialog.component';
import { FormulaPropagationHpListDialogComponent } from '../components/formula-propagation-hp-list-dialog/formula-propagation-hp-list-dialog.component';
import { HousePlanFilter } from './house-plans.model';
import {
  createInitHpeMappingContext,
  HPE_CCC_ID,
  HpeCcOrderDetails,
  HpeCostCenter,
  HpeDetails,
  hpeDetailsFromInitDto,
  hpeDetailsFromRecalculateDto,
  HpeDetailsGetComponents,
  hpeDetailsGetComponentsFromDto,
  HpeDetailsGetCostCenter,
  hpeDetailsGetCostCenterFromDto,
  HpeDetailsGetCostCenterRequest,
  hpeDetailsGetCostCenterRequestToDto,
  HpeDetailsInit,
  hpeDetailsInitToDto,
  HpeDetailsProcess,
  HpeDetailsSave,
  hpeDetailsSaveToDto,
  hpeDetailsToInitCostCenterRequestDto,
  hpeFormulaVariableFromTopDto,
  HpeMappingContext,
  HpSourceJobListItem,
  HpeUomRefItem,
  hpeUomRefItemFromDto, HpeTopRefItem, hpeTopRefItemFromDto,
} from './hpe-details.model';

export interface HpeOrderDetailsDialogConfig {
  hpCode: string;
  hpName: string;
  ccRequest: HpeDetailsGetCostCenterRequest;
}

export interface HousePlanEstimateCreateDialogConfig {
  housePlanId: ID;
  isCloneFromJobAllowed: boolean;
  useSalesEstimates: boolean;
}

@Injectable()
export class HousePlanEstimateDetailsBusinessService implements InitViewService {
  constructor(private gridLayoutService: GridLayoutService,
              private translations: TranslationService,
              private dictionaries: DictionaryService,
              private housePlanRest: HousePlanRestService,
              private formulaContextLoader: FormulaContextLoader,
              private housePlanEstimateRest: HousePlanEstimateRestService,
              private uomRest: UomRestService,
              private topRest: TakeOffParamsRestService,
              private report: ReportService,
              private jobRestService: JobRestService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const { sourceHousePlanId, sourceJobId, initType, priceBookId } = route.queryParams;
    const { housePlanId } = route.params;

    return expandObservable({
      hpeComponentGridLayout: this.gridLayoutService.get('hpComponents'),
      hpeItemGridLayout: this.gridLayoutService.get('hpItems'),
      details: this.init({ priceBookId, housePlanId, sourceHousePlanId, sourceJobId, initType }),
      reportUrl: this.report.generateUrl(
        REPORT_HOUSE_PLAN_BILL_OF_MATERIALS,
        [reportIdField('Template', housePlanId)]),
      gtoUomDict: this.dictionaries.getByEnum(GtoUom),
    }).pipe(switchMap$((expanded) => expandObservable({
      ...expanded,
      formulaContext: this.formulaContextLoader.loadForEstimatingWithVariables(expanded.details.tops
        .map(hpeFormulaVariableFromTopDto)),
    })));
  }

  initOrderDetailsDialog(route: DialogRouteSnapshot<HpeOrderDetailsDialogConfig>): Observable<HpeCcOrderDetails> {
    const { ccRequest } = route.input;

    return this.getOrderDetails(ccRequest);
  }

  init(init: HpeDetailsInit): Observable<HpeDetails> {
    return this.housePlanEstimateRest.init(hpeDetailsInitToDto(init)).pipe(
      map$(hpeDetailsFromInitDto),
      map$((details) => this.injectComponentCostCenter(details)),
      map$((details) => ({
        ...details,
        priceBookId: init.priceBookId,
      })));
  }

  initWithCostCenter(init: HpeDetailsInit,
                     costCenterId: Maybe<ID>): Observable<HpeDetailsProcess> {
    return this.init(init).pipe(
      switchMap$((details) => {
        if (costCenterId) {
          const mappingContext = createInitHpeMappingContext(details);
          return this.loadCostCenter(hpeDetailsToInitCostCenterRequestDto(details, costCenterId), mappingContext).pipe(
            map$((cc) => ({
              ...details,
              initialCostCenterId: costCenterId,
              initialCostCenter: cc,
            })));
        } else {
          return [details];
        }
      }));
  }

  loadHousePlans(filter: HousePlanFilter, query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.housePlanRest
      .findAllAsAutocompleteDataSource({ ...filter, searchString: query.filter }).select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map(withDisplayAsNameAndDescription)));
  }

  loadJobs(query: FilteredDataQuery<string>): Observable<HpSourceJobListItem[]> {
    return this.jobRestService
      .findAllAsAutocompleteDataSource({
        searchString: query.filter,
        showFinalized: true,
        showDpp: true,
        showActive: true,
        showNotProceeding: true,
      }).select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map((item) => ({ ...item, name: `${item.jobNumber} ${item.contactName}` }))));
  }

  loadCostCenter(request: HpeDetailsGetCostCenterRequest,
                 mappingContext: HpeMappingContext): Observable<HpeDetailsGetCostCenter> {
    return this.housePlanEstimateRest.getCostCenter(
      request.housePlanId,
      request.costCenterId,
      hpeDetailsGetCostCenterRequestToDto(request),
    ).pipe(map$((cc) => hpeDetailsGetCostCenterFromDto(request.costCenterId, cc, mappingContext)));
  }

  getOrderDetails(request: HpeDetailsGetCostCenterRequest): Observable<HpeCcOrderDetails> {
    return this.housePlanEstimateRest.getOrderDetails(
      request.housePlanId,
      request.costCenterId,
      hpeDetailsGetCostCenterRequestToDto(request));
  }

  getComponents(housePlanId: ID,
                components: ComponentRequest[],
                mappingContext: HpeMappingContext): Observable<HpeDetailsGetComponents> {
    return this.housePlanEstimateRest.getComponents(housePlanId, components).pipe(
      map$((response) => hpeDetailsGetComponentsFromDto(response, mappingContext)));
  }

  getUom(uomId: ID): Observable<HpeUomRefItem> {
    return this.uomRest.getShared(uomId).pipe(map$(hpeUomRefItemFromDto));
  }

  getTop(topId: ID): Observable<HpeTopRefItem> {
    return this.topRest.getShared(topId).pipe(map$(hpeTopRefItemFromDto));
  }

  generateItemIds(housePlanId: ID, count: number): Observable<ID[]> {
    return this.housePlanEstimateRest.generateItemIds(housePlanId, count).pipe(map$((resp) => resp.hpItemIds));
  }

  /**
   * On process we load new page initialization data (as for general page initialization)
   * and initial cost center simultaneously.
   */
  process(model: HpeDetailsSave,
          costCenterId: Maybe<ID>,
          mappingContext: HpeMappingContext): Observable<HpeDetailsProcess> {
    return this.housePlanEstimateRest.save(hpeDetailsSaveToDto(model, true)).pipe(
      map$((resp) => hpeDetailsFromRecalculateDto(resp, mappingContext)),
      map$((details) => this.injectComponentCostCenter(details)),
      switchMap$((details) => {
        if (costCenterId) {
          return this.loadCostCenter(hpeDetailsToInitCostCenterRequestDto(details, costCenterId), mappingContext).pipe(
            map$((cc) => ({
              ...details,
              initialCostCenterId: costCenterId,
              initialCostCenter: cc,
              priceBookId: model.priceBookId,
            })));
        } else {
          return [{
            ...details,
            priceBookId: model.priceBookId,
          }];
        }
      }));
  }

  recalculate(model: HpeDetailsSave, mappingContext: HpeMappingContext): Observable<HpeDetails> {
    return this.housePlanEstimateRest.save(hpeDetailsSaveToDto(model, false)).pipe(
      map$((resp) => hpeDetailsFromRecalculateDto(resp, mappingContext)),
      map$((details) => this.injectComponentCostCenter(details)));
  }

  private createComponentCc(memo?: string): HpeCostCenter {
    return {
      hpCostCenterId: HPE_CCC_ID,
      costCenterId: HPE_CCC_ID,
      code: 0,
      name: this.translations.get('lbl_components'),
      version: 0,
      preloaded: true,
      count: 0,
      memo,
    };
  }

  private injectComponentCostCenter(details: HpeDetails): HpeDetails {
    return ({
      ...details,
      hpCostCenters: [this.createComponentCc(details.memo)].concat(details.hpCostCenters),
    });
  }
}

export const formulaPropagationDialogRoute = createDialogRoute({
  component: FormulaPropagationDialogComponent,
});

export const formulaPropagationHpListDialogRoute = createDialogRoute({
  component: FormulaPropagationHpListDialogComponent,
});

@Injectable()
export class FormulaPropagationBusinessService {
  constructor(private restService: HousePlanEstimateRestService) {

  }

  findAllAsDataSource(showDeleted: boolean): SlicedDataSource<ComponentPropagateFormulaRequestCustomDto> {
    return this.restService.findAllAsDataSource({ showDeleted });
  }
}
